import "../styles/tokens.css";
import "../styles/base.css";
import "../styles/features/legacy-utilities.css";
import "../styles/features/pdp.css";
import "../styles/features/product-card.css";
import "../styles/routes/collection.css";
import "../styles/routes/home-countdown-slider.css";
import "../styles/features/legacy-remaining.css";

import { onDomReady } from "../core/dom";
import { bootApplication } from "../core/boot";
import { detectTemplateNameFromBody } from "../core/route-template";
import { initQuickAddSheet } from "../features/quick-add-sheet/init";
import { initProductCards } from "../features/product-card";
import { initIntegrations } from "../integrations";
import { openPreferredCartDrawer } from "../features/cart/open-preferred-drawer";
import {
  persistDisplayCurrencyPreference,
  scheduleActiveCurrencyLabelSync
} from "../features/localization/sync-active-currency-label";
import type { TemplateName } from "../types/route-context";

const INTERACTIVE_UI_DISABLED_TEMPLATES = new Set<TemplateName>([
  "password",
  "gift_card",
  "customer",
  "unknown"
]);

let cartAddFetchPatched = false;
let localizationFormGuardInstalled = false;

function getCurrencyCodeFromOption(option: HTMLElement): string | null {
  const explicitCurrencyCode = option.dataset.currencyCode?.trim().toUpperCase();
  if (explicitCurrencyCode && /^[A-Z]{3}$/.test(explicitCurrencyCode)) {
    return explicitCurrencyCode;
  }

  const optionText = option.textContent?.toUpperCase() ?? "";
  const currencyMatch = optionText.match(/\b[A-Z]{3}\b/);
  return currencyMatch?.[0] ?? null;
}

/**
 * Submits Shopify's localization form the supported way (POST /localization).
 * On live storefronts this keeps country + currency in sync for Markets; a
 * currency-only URL reload can leave an invalid context and surface a generic
 * Shopify error page.
 */
function submitLocalizationFormNatively(form: HTMLFormElement): void {
  if (typeof form.requestSubmit === "function") {
    form.requestSubmit();
    return;
  }

  form.submit();
}

function isCartAddUrl(url: string): boolean {
  if (!url) {
    return false;
  }

  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.pathname.endsWith("/cart/add.js");
  } catch {
    return url.includes("/cart/add.js");
  }
}

function extractFetchInputUrl(input: Parameters<typeof window.fetch>[0]): string {
  if (typeof input === "string") {
    return input;
  }

  if (input instanceof URL) {
    return input.toString();
  }

  if (typeof Request !== "undefined" && input instanceof Request) {
    return input.url;
  }

  return "";
}

function forceOpenCartDrawer(): void {
  openPreferredCartDrawer();
}

function installCartAddDrawerGuard(): void {
  if (typeof window.fetch !== "function") {
    return;
  }

  document.body.addEventListener("baseline:modalcart:afteradditem", () => {
    forceOpenCartDrawer();
  });

  if (cartAddFetchPatched) {
    return;
  }

  const nativeFetch: typeof window.fetch = window.fetch.bind(window);
  window.fetch = (async (...args: Parameters<typeof window.fetch>) => {
    const response = await nativeFetch(...args);

    try {
      const requestUrl = extractFetchInputUrl(args[0]);
      if (response.ok && (isCartAddUrl(requestUrl) || isCartAddUrl(response.url))) {
        window.setTimeout(forceOpenCartDrawer, 0);
      }
    } catch {
      // Ignore fetch interception failures; add-to-cart request result remains unchanged.
    }

    return response;
  }) as typeof window.fetch;

  cartAddFetchPatched = true;
}

function installLocalizationFormGuard(): void {
  if (localizationFormGuardInstalled) {
    return;
  }

  document.addEventListener(
    "click",
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const option = target.closest<HTMLElement>("[data-disclosure-option][data-value]");
      if (!option) {
        return;
      }

      const disclosure = option.closest<HTMLElement>(
        "[data-disclosure-country], [data-disclosure-locale]"
      );
      if (!disclosure) {
        return;
      }

      const form = disclosure.closest<HTMLFormElement>("[data-disclosure-form]");
      const input = disclosure.querySelector<HTMLInputElement>("[data-disclosure-input]");
      const nextValue = option.dataset.value?.trim();
      if (!form || !input) {
        return;
      }

      const formTypeInput = form.querySelector<HTMLInputElement>("input[name='form_type']");
      if (formTypeInput && formTypeInput.value !== "localization") {
        return;
      }

      const isCountryDisclosure = disclosure.hasAttribute("data-disclosure-country");
      const selectedCurrencyCode = getCurrencyCodeFromOption(option);

      if (isCountryDisclosure && selectedCurrencyCode) {
        event.preventDefault();
        event.stopPropagation();

        persistDisplayCurrencyPreference(selectedCurrencyCode);

        if (nextValue) {
          input.value = nextValue;
        }

        document.cookie = `cart_currency=${selectedCurrencyCode}; path=/; max-age=1209600; SameSite=Lax`;
        submitLocalizationFormNatively(form);
        return;
      }

      if (!nextValue) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      input.value = nextValue;

      if (typeof form.requestSubmit === "function") {
        form.requestSubmit();
        return;
      }

      form.submit();
    },
    true
  );

  localizationFormGuardInstalled = true;
}

onDomReady(() => {
  const body = document.body as HTMLBodyElement | null;
  if (!body) {
    return;
  }

  const template = detectTemplateNameFromBody(body);
  initIntegrations(template);
  installLocalizationFormGuard();
  scheduleActiveCurrencyLabelSync();

  if (!INTERACTIVE_UI_DISABLED_TEMPLATES.has(template)) {
    initProductCards(document);
    const quickAddSheet = initQuickAddSheet();
    installCartAddDrawerGuard();
    const rebindProductCardUi = (): void => {
      initProductCards(document);
      quickAddSheet?.bindButtons(document);
    };
    document.addEventListener("baseline:collection:grid-updated", rebindProductCardUi);
    document.addEventListener("baseline:product:recommendations-updated", rebindProductCardUi);
  }

  void bootApplication();
});
